

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    FormControlLabel,
    Switch,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewItineraryModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        trip_id: '',
        total_days: '',
        title: '',
        description: '',
        primary_attractions: '',
        image_url: '',
        meals_summary: '',
        location: '',
        travefy_url: '',
        travefy_sync: false,
        type: 'Plotpackers',
        influencer_account_id: '',
        trip_option_id: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [trips, setTrips] = useState([]);
    const [tripOptions, setTripOptions] = useState([]);
    const [influencers, setInfluencers] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchData();
            setFormData({
                name: '',
                trip_id: '',
                total_days: '',
                title: '',
                description: '',
                primary_attractions: '',
                image_url: '',
                meals_summary: '',
                location: '',
                travefy_url: '',
                travefy_sync: false,
                type: 'Plotpackers',
                influencer_account_id: '',
                trip_option_id: ''
            });
            setFormErrors({});
        }
    }, [isOpen]);

    useEffect(() => {
        if (formData.trip_id) {
            fetchTripOptions(formData.trip_id);
        } else {
            setTripOptions([]);
        }
    }, [formData.trip_id]);

    const fetchData = async () => {
        setLoadingData(true);
        try {
            const [tripsRes, accountsRes] = await Promise.all([
                axiosInstance.get('/trips/'),
                axiosInstance.get('/accounts/?dropdown=true')
            ]);
            setTrips(tripsRes.data);
            setInfluencers(accountsRes.data.filter(acc => 
                (acc.account_type || "").toLowerCase() === 'influencer' || 
                (acc.type || "").toLowerCase() === 'influencer'
            ));
        } catch (error) {
            console.error('Error fetching modal data:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const fetchTripOptions = async (tripId) => {
        try {
            const response = await axiosInstance.get(`/trip-options/?trip_id=${tripId}`);
            setTripOptions(response.data || []);
        } catch (error) {
            console.error('Error fetching trip options:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: newValue
            };
            // Clear influencer_account_id when type changes to Plotpackers
            if (name === 'type' && newValue === 'Plotpackers') {
                updated.influencer_account_id = '';
            }
            // Clear trip_option_id when trip_id changes
            if (name === 'trip_id') {
                updated.trip_option_id = '';
            }
            return updated;
        });
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.trip_id) errors.trip_id = 'Trip is required';
        if (!formData.total_days) errors.total_days = 'Total Days is required';
        if (!formData.trip_option_id) errors.trip_option_id = 'Trip Option is required';
        if (formData.type === 'Influencer' && !formData.influencer_account_id) {
            errors.influencer_account_id = 'Account (Influencer) is required when Type is Influencer';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                name: formData.name,
                trip_id: parseInt(formData.trip_id),
                total_days: parseInt(formData.total_days),
                title: formData.title || null,
                description: formData.description || null,
                primary_attractions: formData.primary_attractions || null,
                image_url: formData.image_url || null,
                meals_summary: formData.meals_summary || null,
                location: formData.location || null,
                travefy_url: formData.travefy_url || null,
                travefy_sync: formData.travefy_sync,
                type: formData.type || null,
                influencer_account_id: formData.influencer_account_id ? parseInt(formData.influencer_account_id) : null,
                trip_option_id: parseInt(formData.trip_option_id)
            };

            await onSave(payload);
            onClose();
        } catch (error) {
            console.error('Error saving Itinerary:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Itinerary',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '900px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Itinerary</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required error={!!formErrors.trip_id}>
                                        <InputLabel>Trip</InputLabel>
                                        <Select
                                            name="trip_id"
                                            value={formData.trip_id}
                                            onChange={handleInputChange}
                                            label="Trip"

                                            disabled={loadingData}
                                        >
                                            <MenuItem value=""><em>Select Trip</em></MenuItem>
                                            {trips.map(t => (
                                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Total Days"
                                        name="total_days"
                                        value={formData.total_days}
                                        onChange={handleInputChange}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                        error={!!formErrors.total_days}
                                        helperText={formErrors.total_days}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required error={!!formErrors.trip_option_id}>
                                        <InputLabel>Trip Option</InputLabel>
                                        <Select
                                            name="trip_option_id"
                                            value={formData.trip_option_id}
                                            onChange={handleInputChange}
                                            label="Trip Option"

                                            disabled={!formData.trip_id || loadingData}
                                        >
                                            <MenuItem value=""><em>Select Trip Option</em></MenuItem>
                                            {tripOptions.map(opt => (
                                                <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleInputChange}
                                            label="Type"

                                        >
                                            <MenuItem value="Plotpackers">Plotpackers</MenuItem>
                                            <MenuItem value="Influencer">Influencer</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {formData.type === 'Influencer' && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" required error={!!formErrors.influencer_account_id}>
                                            <InputLabel>Account (Influencer)</InputLabel>
                                            <Select
                                                name="influencer_account_id"
                                                value={formData.influencer_account_id}
                                                onChange={handleInputChange}
                                                label="Account (Influencer)"

                                                disabled={loadingData}
                                            >
                                                <MenuItem value=""><em>Select Influencer</em></MenuItem>
                                                {influencers.map(inf => (
                                                    <MenuItem key={inf.id} value={inf.id}>{inf.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Location"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Primary Attractions"
                                        name="primary_attractions"
                                        value={formData.primary_attractions}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Image URL"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Meals Summary"
                                        name="meals_summary"
                                        value={formData.meals_summary}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Travefy URL"
                                        name="travefy_url"
                                        value={formData.travefy_url}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <MDBox mt={2}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.travefy_sync}
                                                    onChange={handleInputChange}
                                                    name="travefy_sync"
                                                />
                                            }
                                            label="Travefy Sync"
                                        />
                                    </MDBox>
                                </Grid>
                            </Grid>

                            <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
                                <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={isSaving}>
                                    Cancel
                                </MDButton>
                                <MDButton variant="gradient" color="info" type="submit" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={24} /> : 'Save'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewItineraryModal;

